import {
  getPackageWithMinStps,
  selectAllowUpgrade,
  Selectors3asFPermissions,
  selectStpLimit,
} from '@va/aaas/shared/selectors';
import { PlanIndexes, PlanNames } from '@va/constants';
import { startUpgradeProcess } from '@va/dashboard/actions/app';
import { getCurrentPlanName } from '@va/dashboard/selectors/app';
import { UpgradePlanTooltipComponent } from '@va/dashboard/ui/components';
import { LocationIcon, LockIcon } from '@va/icons';
import { useLocale, useTranslate } from '@va/localization';
import { setUpgradePlanIndex } from '@va/standalone/shared/actions';
import { hasPermission } from '@va/standalone/shared/selectors';
import {
  fontWeights,
  IconWithBackground,
  paragraphSizes,
  ParagraphWithTooltip,
  Progress,
  ProgressBar,
} from '@va/ui/design-system';
import { addNumberSeparator } from '@va/util/helpers';
import { selectCurrentPlanName } from '@va/wix-mini/shared/selectors';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export const UpgradePlanTooltip = () => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const activePlanName = useSelector(getCurrentPlanName);
  const hasUpgradePermission = useSelector(hasPermission);

  return (
    <UpgradePlanTooltipComponent
      requiredPlanText={translate('all.upgradePlanTooltip.requiredPlan', { planName: PlanNames.Advanced })} // TODO Change tolgee translation to accept variable
      currentPlanText={translate('all.upgradePlanTooltip.currentPlan', { planName: activePlanName })}
      title={translate('all.privacyCenter.privacyLevel.privacyMode.basedOnCountries.title')}
      text={translate('all.privacyCenter.privacyLevel.privacyMode.basedOnCountries.info')}
      image={<FeatureRepresentation />}
      onUpgradeClick={() => {
        if (!hasUpgradePermission) return;
        dispatch(setUpgradePlanIndex(PlanIndexes.Advanced));
        dispatch(startUpgradeProcess());
      }}
    />
  );
};

export const AaasMinCreditsTooltip = () => {
  const translate = useTranslate();
  const stpLimit = useSelector(selectStpLimit);
  const minStps = useSelector(Selectors3asFPermissions.limit.countryBasedPrivacy);
  const allowUpgrade = useSelector(selectAllowUpgrade);
  const { locale } = useLocale();
  const dispatch = useDispatch();
  // @ts-ignore
  const suggestedPlanToUpgrade = useSelector((state) => getPackageWithMinStps(state, minStps));
  const currentPlanName = useSelector(selectCurrentPlanName);
  const hasInfiniteCredits = useMemo(() => stpLimit === -1, [stpLimit]);

  const onIntpUpgrade = useCallback(() => {
    dispatch(startUpgradeProcess());
  }, [dispatch]);

  const requiredPlanText = useMemo(() => {
    if (!suggestedPlanToUpgrade) {
      return translate('aaas.requiredCreditsCount', { count: addNumberSeparator(minStps, locale) });
    }

    return translate('aaas.requiredPlan', { planName: suggestedPlanToUpgrade?.name });
  }, [locale, minStps, suggestedPlanToUpgrade, translate]);

  const currentPlanText = useMemo(() => {
    if (!suggestedPlanToUpgrade) {
      return translate('aaas.currentCreditsCount', {
        count: hasInfiniteCredits ? '∞' : addNumberSeparator(stpLimit, locale),
      });
    }

    return translate('aaas.currentPlan', { planName: currentPlanName });
  }, [currentPlanName, hasInfiniteCredits, locale, stpLimit, suggestedPlanToUpgrade, translate]);

  return (
    <UpgradePlanTooltipComponent
      requiredPlanText={requiredPlanText}
      currentPlanText={currentPlanText}
      title={translate('all.privacyCenter.privacyLevel.privacyMode.basedOnCountries.title')}
      text={translate('all.privacyCenter.privacyLevel.privacyMode.basedOnCountries.info')}
      image={<FeatureRepresentation />}
      onUpgradeClick={allowUpgrade ? onIntpUpgrade : undefined}
    />
  );
};

const FeatureRepresentation = () => {
  const translate = useTranslate();

  return (
    <div className='text-white flex gap-3 rounded-30px bg-black-dark/50 pt-4 overflow-clip max-w-full'>
      <PrivacyLevelDummy
        title={`${translate('label.active')} : ${translate('onboarding.privacyLevels.defaultPrivacy.title')}`}
        totalCountriesText={translate('all.privacyCenter.privacyLevel.countriesCount', { count: 120 })}
        lockedCountriesText={translate('all.privacyCenter.privacyLevel.lockedCountriesCount', { count: 36 })}
        className='min-w-[60%] rounded-tr-30px rounded-bl-30px'
      />
      <PrivacyLevelDummy
        title={translate('onboarding.privacyLevels.basicPrivacy.title')}
        className='rounded-tl-30px rounded-br-30px'
        securityLevel={[
          { color: '#9ca3af', percentage: 50 },

          { color: '#ffffff', percentage: 50 },
        ]}
        totalCountriesText={translate('all.privacyCenter.privacyLevel.countriesCount', { count: 24 })}
        lockedCountriesText={translate('all.privacyCenter.privacyLevel.lockedCountriesCount', { count: 6 })}
      />
    </div>
  );
};

const PrivacyLevelDummy = ({
  className,
  title,
  securityLevel = [
    { color: '#9ca3af', percentage: 25 },
    { color: '#ffffff', percentage: 75 },
  ],
  totalCountriesText,
  lockedCountriesText,
}: {
  className?: string;
  title: string;
  securityLevel?: Progress[];
  totalCountriesText: string;
  lockedCountriesText: string;
}) => {
  return (
    <div className={classNames('flex flex-col bg-white/25 p-4 gap-3', className)}>
      <div className='flex flex-col gap-2'>
        <div className='flex justify-between gap-2 items-center'>
          <LockIcon color='#FFFFFF' className='shrink-0 h-3 w-3' />
          <ProgressBar progress={securityLevel} className='!h-1' />
          <PrivacyCheckMarkIcon className='shrink-0 ml-3' />
        </div>
        <ParagraphWithTooltip weight={fontWeights.medium}>{title}</ParagraphWithTooltip>
      </div>
      <div className='flex gap-9px'>
        <IconWithBackground className='bg-orange-pure shrink-0' icon={() => <LocationIcon color='#FFFFFF' />} />
        <div className='flex flex-col justify-center truncate'>
          <ParagraphWithTooltip size={paragraphSizes.tiny}>{totalCountriesText}</ParagraphWithTooltip>
          <ParagraphWithTooltip size={paragraphSizes.tiny}>{lockedCountriesText}</ParagraphWithTooltip>
        </div>
      </div>
    </div>
  );
};

const PrivacyCheckMarkIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      className={className}
      width='18'
      height='18'
      viewBox='0 0 18 18'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <circle cx='9' cy='9' r='8.25' stroke='#F66F1E' strokeWidth='1.5' />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M9 14.625C12.1066 14.625 14.625 12.1066 14.625 9C14.625 5.8934 12.1066 3.375 9 3.375C5.8934 3.375 3.375 5.8934 3.375 9C3.375 12.1066 5.8934 14.625 9 14.625ZM8.83525 11.0852L12.2102 7.71025L11.4148 6.91475L8.4375 9.892L6.58525 8.03975L5.78975 8.83525L8.03975 11.0852L8.4375 11.483L8.83525 11.0852Z'
        fill='white'
      />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M9 14.625C12.1066 14.625 14.625 12.1066 14.625 9C14.625 5.8934 12.1066 3.375 9 3.375C5.8934 3.375 3.375 5.8934 3.375 9C3.375 12.1066 5.8934 14.625 9 14.625ZM9 15.75C12.7279 15.75 15.75 12.7279 15.75 9C15.75 5.27208 12.7279 2.25 9 2.25C5.27208 2.25 2.25 5.27208 2.25 9C2.25 12.7279 5.27208 15.75 9 15.75Z'
        fill='white'
      />
    </svg>
  );
};
